import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';

import { ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import type { FittingFeatureProperties } from '@/components/map/utils/fittingFeatureProperties';
import { FITTINGS_SOURCE_ID } from '@/hooks/useFittingsLayer';
import { createFeatureHoverTween } from '@/utils/featureHoverTween';

export interface FittingInteractionState {
    hoveredFitting: FittingFeatureProperties | null;
    /** Geographic [lng, lat] of the hovered fitting — the MapLibre `<Popup>` anchor. */
    hoveredFittingLngLat: [number, number] | null;
}

/**
 * Tracks the fitting feature under the cursor so MapView can show an informational card
 * (FittingPopup) with the fitting's type — the only distinguishing data left now that all
 * fittings render as the same uniform marker.
 *
 * Exposes the fitting's geographic point; the MapLibre `<Popup>` projects and tracks it on
 * pan/zoom itself, so no screen-pixel bookkeeping is needed here. A ref-based id guard avoids
 * redundant state updates while the cursor stays over the same fitting.
 *
 * Hover only — no selection or click behaviour. Re-attaches its listeners on `style.load`
 * because a base-map switch recreates all layers. Safe when the map is not ready or the
 * layer does not exist.
 */
export function useFittingInteractions(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
): FittingInteractionState {
    const [hoveredFitting, setHoveredFitting] = useState<FittingFeatureProperties | null>(null);
    const [hoveredLngLat, setHoveredLngLat] = useState<[number, number] | null>(null);

    const hoveredIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (!isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        const hoverTween = createFeatureHoverTween(mlMap, FITTINGS_SOURCE_ID);

        const clearHover = () => {
            if (hoveredIdRef.current === null) return;
            hoveredIdRef.current = null;
            hoverTween.setHovered(null);
            setHoveredFitting(null);
            setHoveredLngLat(null);
        };

        const handleMouseMove = (e: maplibregl.MapLayerMouseEvent) => {
            // No hover while the camera moves — a card created mid-drag/zoom/fly would
            // pan with the map, anchored to a point sliding out from under the cursor.
            if (mlMap.isMoving()) return;
            const feature = e.features?.[0];
            if (!feature) return;

            const props = feature.properties as unknown as FittingFeatureProperties;
            const id = props.id;
            // No state update while the cursor stays over the same fitting.
            if (id === hoveredIdRef.current) return;

            hoveredIdRef.current = id;
            hoverTween.setHovered(id);
            const geometry = feature.geometry;
            const lngLat: [number, number] =
                geometry.type === 'Point' ? (geometry.coordinates as [number, number]) : [e.lngLat.lng, e.lngLat.lat];
            setHoveredFitting(props);
            setHoveredLngLat(lngLat);
        };

        // Any camera movement (drag, zoom, fly-to, ease-to) dismisses the hover card and
        // ramps the tween back down; the isMoving() guard above keeps it dismissed until
        // the movement ends, after which the next mousemove recreates hover normally.
        const handleMoveStart = () => {
            clearHover();
        };

        // Registered once — delegated layer listeners re-resolve their layer ids on every
        // event, so they survive a style reload. Re-registering them on `style.load` would
        // duplicate them: MapLibre dedupes plain listeners but not delegated ones.
        mlMap.on('mousemove', ORBIT_LAYER_IDS.fittings, handleMouseMove);
        mlMap.on('mouseleave', ORBIT_LAYER_IDS.fittings, clearHover);
        mlMap.on('movestart', handleMoveStart);

        return () => {
            hoverTween.destroy();
            mlMap.off('mousemove', ORBIT_LAYER_IDS.fittings, handleMouseMove);
            mlMap.off('mouseleave', ORBIT_LAYER_IDS.fittings, clearHover);
            mlMap.off('movestart', handleMoveStart);
        };
    }, [isMapLoaded, mapRef]);

    return { hoveredFitting, hoveredFittingLngLat: hoveredLngLat };
}
